import {
  Add,
  DeleteOutline,
  ExpandMore,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { BodyMode, FlowStep, HttpMethod } from '../models';

const METHODS: HttpMethod[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
const BODY_MODES: { value: BodyMode; label: string }[] = [
  { value: 'none', label: 'Sin cuerpo' },
  { value: 'json', label: 'JSON' },
  { value: 'multipart', label: 'Multipart' },
];

interface Props {
  step: FlowStep;
  index: number;
  total: number;
  onChange: (step: FlowStep) => void;
  onRemove: () => void;
  onMove: (offset: number) => void;
}

const StepEditor = ({
  step,
  index,
  total,
  onChange,
  onRemove,
  onMove,
}: Props) => {
  const set = <K extends keyof FlowStep>(key: K, value: FlowStep[K]) =>
    onChange({ ...step, [key]: value });

  const headerLines = Object.entries(step.headers)
    .map(([name, value]) => `${name}: ${value}`)
    .join('\n');

  const setHeaders = (text: string) => {
    const headers: Record<string, string> = {};
    text.split('\n').forEach((line) => {
      const separator = line.indexOf(':');
      if (separator < 1) return;
      const name = line.slice(0, separator).trim();
      const value = line.slice(separator + 1).trim();
      if (name) headers[name] = value;
    });
    set('headers', headers);
  };

  return (
    <Accordion disableGutters>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Stack
          direction='row'
          spacing={1}
          alignItems='center'
          sx={{ width: '100%', pr: 1 }}
        >
          <Chip size='small' label={step.method} />
          <Typography
            sx={{ flexGrow: 1, opacity: step.enabled ? 1 : 0.5 }}
            noWrap
          >
            {index + 1}. {step.name || step.path}
          </Typography>
          <Tooltip title={step.enabled ? 'Se ejecuta' : 'Se saltea'}>
            <Switch
              size='small'
              checked={step.enabled}
              onClick={(event) => event.stopPropagation()}
              onChange={(event) => set('enabled', event.target.checked)}
            />
          </Tooltip>
        </Stack>
      </AccordionSummary>

      <AccordionDetails>
        <Stack spacing={2}>
          <Stack direction='row' spacing={1}>
            <TextField
              size='small'
              label='Nombre'
              value={step.name}
              onChange={(event) => set('name', event.target.value)}
              fullWidth
            />
            <IconButton
              size='small'
              disabled={index === 0}
              aria-label='Subir'
              onClick={() => onMove(-1)}
            >
              <ArrowUpward fontSize='small' />
            </IconButton>
            <IconButton
              size='small'
              disabled={index === total - 1}
              aria-label='Bajar'
              onClick={() => onMove(1)}
            >
              <ArrowDownward fontSize='small' />
            </IconButton>
            <IconButton size='small' aria-label='Eliminar' onClick={onRemove}>
              <DeleteOutline fontSize='small' />
            </IconButton>
          </Stack>

          <Stack direction='row' spacing={1}>
            <TextField
              select
              size='small'
              label='Método'
              value={step.method}
              onChange={(event) =>
                set('method', event.target.value as HttpMethod)
              }
              sx={{ minWidth: 110 }}
            >
              {METHODS.map((method) => (
                <MenuItem key={method} value={method}>
                  {method}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size='small'
              label='Ruta'
              value={step.path}
              onChange={(event) => set('path', event.target.value)}
              helperText='Relativa al back de business, o una URL completa'
              fullWidth
            />
            <TextField
              size='small'
              label='Status esperado'
              value={step.expectStatus}
              onChange={(event) => set('expectStatus', event.target.value)}
              helperText='Vacío = cualquier 2xx'
              sx={{ minWidth: 150 }}
            />
          </Stack>

          <TextField
            size='small'
            label='Headers'
            value={headerLines}
            onChange={(event) => setHeaders(event.target.value)}
            multiline
            minRows={2}
            helperText='Uno por línea, "Nombre: valor". Sin Authorization va el token de tu sesión.'
            fullWidth
          />

          <Stack direction='row' spacing={1} alignItems='flex-start'>
            <TextField
              select
              size='small'
              label='Cuerpo'
              value={step.bodyMode}
              onChange={(event) =>
                set('bodyMode', event.target.value as BodyMode)
              }
              sx={{ minWidth: 150 }}
            >
              {BODY_MODES.map((mode) => (
                <MenuItem key={mode.value} value={mode.value}>
                  {mode.label}
                </MenuItem>
              ))}
            </TextField>

            {step.bodyMode === 'json' && (
              <TextField
                size='small'
                label='JSON'
                value={step.body}
                onChange={(event) => set('body', event.target.value)}
                multiline
                minRows={4}
                fullWidth
                slotProps={{
                  input: { sx: { fontFamily: 'monospace', fontSize: 13 } },
                }}
              />
            )}
          </Stack>

          {step.bodyMode === 'multipart' && (
            <Box>
              <Typography variant='body2' color='text.secondary' mb={1}>
                Campos del multipart. Los de tipo archivo suben un PNG de 1x1
                generado al vuelo, que es lo que piden la firma y la foto.
              </Typography>
              <Stack spacing={1}>
                {step.fields.map((field, fieldIndex) => (
                  <Stack
                    key={`${field.name}-${fieldIndex}`}
                    direction='row'
                    spacing={1}
                  >
                    <TextField
                      size='small'
                      label='Campo'
                      value={field.name}
                      onChange={(event) => {
                        const fields = [...step.fields];
                        fields[fieldIndex] = {
                          ...field,
                          name: event.target.value,
                        };
                        set('fields', fields);
                      }}
                      sx={{ minWidth: 220 }}
                    />
                    <TextField
                      select
                      size='small'
                      label='Tipo'
                      value={field.kind}
                      onChange={(event) => {
                        const fields = [...step.fields];
                        fields[fieldIndex] = {
                          ...field,
                          kind: event.target.value as 'text' | 'png',
                        };
                        set('fields', fields);
                      }}
                      sx={{ minWidth: 120 }}
                    >
                      <MenuItem value='text'>Texto</MenuItem>
                      <MenuItem value='png'>Archivo PNG</MenuItem>
                    </TextField>
                    <TextField
                      size='small'
                      label='Valor'
                      value={field.value}
                      disabled={field.kind === 'png'}
                      onChange={(event) => {
                        const fields = [...step.fields];
                        fields[fieldIndex] = {
                          ...field,
                          value: event.target.value,
                        };
                        set('fields', fields);
                      }}
                      fullWidth
                    />
                    <IconButton
                      size='small'
                      aria-label='Quitar campo'
                      onClick={() =>
                        set(
                          'fields',
                          step.fields.filter((_, i) => i !== fieldIndex)
                        )
                      }
                    >
                      <DeleteOutline fontSize='small' />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
              <IconButton
                size='small'
                aria-label='Agregar campo'
                onClick={() =>
                  set('fields', [
                    ...step.fields,
                    { name: '', value: '', kind: 'text' },
                  ])
                }
              >
                <Add fontSize='small' />
              </IconButton>
            </Box>
          )}

          <Divider />

          <Box>
            <Typography variant='body2' color='text.secondary' mb={1}>
              Qué guardar de la respuesta para los pasos siguientes. El camino
              acepta índices: <code>visits.0.id</code>. Vacío guarda la
              respuesta entera.
            </Typography>
            <Stack spacing={1}>
              {step.captures.map((capture, captureIndex) => (
                <Stack
                  key={`${capture.name}-${captureIndex}`}
                  direction='row'
                  spacing={1}
                >
                  <TextField
                    size='small'
                    label='Variable'
                    value={capture.name}
                    onChange={(event) => {
                      const captures = [...step.captures];
                      captures[captureIndex] = {
                        ...capture,
                        name: event.target.value,
                      };
                      set('captures', captures);
                    }}
                    sx={{ minWidth: 200 }}
                  />
                  <TextField
                    size='small'
                    label='Camino en la respuesta'
                    value={capture.path}
                    onChange={(event) => {
                      const captures = [...step.captures];
                      captures[captureIndex] = {
                        ...capture,
                        path: event.target.value,
                      };
                      set('captures', captures);
                    }}
                    fullWidth
                  />
                  <IconButton
                    size='small'
                    aria-label='Quitar captura'
                    onClick={() =>
                      set(
                        'captures',
                        step.captures.filter((_, i) => i !== captureIndex)
                      )
                    }
                  >
                    <DeleteOutline fontSize='small' />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
            <IconButton
              size='small'
              aria-label='Agregar captura'
              onClick={() =>
                set('captures', [...step.captures, { name: '', path: '' }])
              }
            >
              <Add fontSize='small' />
            </IconButton>
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default StepEditor;
